import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { trimAllWhiteCharacters } from 'noddi-util';
import { useAuthContext } from 'noddi-web';

export type ContactDetailField = 'email' | 'phoneNumber';

const CONTACT_UPDATE_VERIFICATION_FLAG = 'users.contact_update_verification';

export const normalizeContactValue = (field: ContactDetailField, value: string): string =>
  field === 'email' ? value.trim().toLowerCase() : trimAllWhiteCharacters(value);

export const useContactVerificationRequired = (): boolean => {
  const { userData } = useAuthContext();
  const { data, error: _evaluateError } = noddiAsync.useGet({
    type: URLKeys.getFeatureFlagEvaluate,
    queryConfig: {
      enabled: userData?.user != null
    }
  });

  return data?.some((flag) => flag.key === CONTACT_UPDATE_VERIFICATION_FLAG && flag.value) ?? false;
};

export const useContactVerification = () => {
  const { userData, updateUserData } = useAuthContext();
  const requiresVerification = useContactVerificationRequired();
  const {
    mutate: sendEmailVerification,
    isPending: isSendingEmailVerification,
    error: sendEmailVerificationError,
    reset: resetSendEmailVerification
  } = noddiAsync.usePost({ type: URLKeys.postSendEmailVerification });
  const { mutate: sendPhoneNumberVerification, isPending: isSendingPhoneNumberVerification } = noddiAsync.usePost({
    type: URLKeys.postSendPhoneNumberVerification
  });
  const {
    mutateAsync: updateContact,
    isPending: isUpdatingContact,
    error: updateContactError,
    reset: resetUpdateContact
  } = noddiAsync.usePost({
    type: URLKeys.postUpdateContact,
    queryConfig: {
      onSuccess: async ({ data: user }) => {
        updateUserData({ ...userData!, user });
        await invalidateQueryExactMatch({ urlKey: URLKeys.getAccount, input: { userId: user.id } });
      }
    }
  });

  const sendVerification = (field: ContactDetailField, value: string, onSuccess: () => void) => {
    const normalizedValue = normalizeContactValue(field, value);
    if (field === 'email') {
      sendEmailVerification({ email: normalizedValue }, { onSuccess });
      return;
    }
    sendPhoneNumberVerification({ phoneNumber: normalizedValue }, { onSuccess });
  };

  const submitContactUpdate = async (field: ContactDetailField, value: string, verificationCode: string) =>
    updateContact(
      field === 'email'
        ? { email: normalizeContactValue(field, value), emailVerificationCode: verificationCode.trim() || undefined }
        : {
            phoneNumber: normalizeContactValue(field, value),
            phoneNumberVerificationCode: verificationCode.trim() || undefined
          }
    );

  return {
    isSendingEmailVerification,
    isSendingPhoneNumberVerification,
    isUpdatingContact,
    resetSendEmailVerification,
    resetUpdateContact,
    sendEmailVerificationError,
    sendVerification,
    submitContactUpdate,
    updateContactError,
    requiresVerification
  };
};
